import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { SectionHero, StatGrid, MetricCard, AdminCard } from '@/components/admin/primitives'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { callEdge } from '@/lib/edgeClient'
import { RefreshCw, Upload, Cpu, Brain, TrendingUp, TrendingDown } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

interface MetricsResponse {
  period: string
  upload_success_pct: number
  pose_success_pct: number
  analyze_success_pct: number
  p95_upload_ms: number
  p95_pose_ms: number
  p95_analyze_ms: number
  total_runs: number
  time_range: {
    start: string
    end: string
  }
}

type TimeWindow = '24h' | '7d' | '30d' | '90d'

const getSuccessStatus = (percentage: number): 'healthy' | 'degraded' | 'critical' => {
  if (percentage >= 95) return 'healthy'
  if (percentage >= 90) return 'degraded'
  return 'critical'
}

const formatLatency = (ms: number): string => {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(2)}s`
  }
  return `${Math.round(ms)}ms`
}

export default function OperationsDashboard() {
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('7d')

  const { data: metrics, isLoading, error, refetch } = useQuery<MetricsResponse>({
    queryKey: ['pipeline-metrics', timeWindow],
    queryFn: async () => {
      // metrics-aggregate uses GET with query params
      const url = `metrics-aggregate?window=${timeWindow}`
      const data = await callEdge(url, {
        method: 'GET',
        authRequired: true,
      })
      return data as MetricsResponse
    },
    refetchInterval: 60000, // Refresh every minute
  })

  return (
    <div className="min-h-screen bg-surface pb-20">
      <div className="max-w-admin mx-auto px-admin-content-padding py-8">
        {/* Hero Section */}
        <SectionHero
          title="Operations Dashboard"
          description="Pipeline success rates and latency metrics"
          gradient="info"
          actions={
            <>
              <Select value={timeWindow} onValueChange={(v) => setTimeWindow(v as TimeWindow)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">24h</SelectItem>
                  <SelectItem value="7d">7d</SelectItem>
                  <SelectItem value="30d">30d</SelectItem>
                  <SelectItem value="90d">90d</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => refetch()} disabled={isLoading} variant="outline" size="sm">
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </>
          }
        />

        {/* Success Rates */}
        <div className="mt-8">
          <h2 className="text-admin-section font-semibold text-foreground mb-4">Success Rates</h2>
          {isLoading ? (
            <StatGrid columns={3}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </StatGrid>
          ) : (
            <StatGrid columns={3}>
              <MetricCard
                title="Upload Success"
                value={`${metrics?.upload_success_pct.toFixed(1) ?? 0}%`}
                description="Video uploads"
                size="large"
                accentColor={
                  metrics ? (metrics.upload_success_pct >= 95 ? 'success' : metrics.upload_success_pct >= 90 ? 'warning' : 'danger') : 'info'
                }
              />
              <MetricCard
                title="Pose Success"
                value={`${metrics?.pose_success_pct.toFixed(1) ?? 0}%`}
                description="Pose detection"
                size="large"
                accentColor={
                  metrics ? (metrics.pose_success_pct >= 95 ? 'success' : metrics.pose_success_pct >= 90 ? 'warning' : 'danger') : 'info'
                }
              />
              <MetricCard
                title="Analysis Success"
                value={`${metrics?.analyze_success_pct.toFixed(1) ?? 0}%`}
                description="AI analysis"
                size="large"
                accentColor={
                  metrics ? (metrics.analyze_success_pct >= 95 ? 'success' : metrics.analyze_success_pct >= 90 ? 'warning' : 'danger') : 'info'
                }
              />
            </StatGrid>
          )}
        </div>

        {/* Latency Metrics */}
        <div className="mt-8">
          <h2 className="text-admin-section font-semibold text-foreground mb-4">P95 Latency</h2>
          {isLoading ? (
            <StatGrid columns={3}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </StatGrid>
          ) : (
            <StatGrid columns={3}>
              <MetricCard
                title="Upload Latency"
                value={metrics ? formatLatency(metrics.p95_upload_ms) : '0ms'}
                description="P95 response time"
                size="large"
                accentColor={
                  metrics ? (metrics.p95_upload_ms > 20000 ? 'danger' : metrics.p95_upload_ms > 15000 ? 'warning' : 'success') : 'info'
                }
              />
              <MetricCard
                title="Pose Latency"
                value={metrics ? formatLatency(metrics.p95_pose_ms) : '0ms'}
                description="P95 response time"
                size="large"
                accentColor={
                  metrics ? (metrics.p95_pose_ms > 20000 ? 'danger' : metrics.p95_pose_ms > 15000 ? 'warning' : 'success') : 'info'
                }
              />
              <MetricCard
                title="Analysis Latency"
                value={metrics ? formatLatency(metrics.p95_analyze_ms) : '0ms'}
                description="P95 response time"
                size="large"
                accentColor={
                  metrics ? (metrics.p95_analyze_ms > 20000 ? 'danger' : metrics.p95_analyze_ms > 15000 ? 'warning' : 'success') : 'info'
                }
              />
            </StatGrid>
          )}
        </div>

        {/* Pipeline Overview */}
        <div className="mt-8">
          <AdminCard title="Pipeline Overview" accentColor="primary">
            {error ? (
              <p className="text-destructive">Error loading metrics: {error instanceof Error ? error.message : 'Unknown error'}</p>
            ) : metrics ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Runs:</span>
                    <span className="ml-2 font-medium">{metrics.total_runs.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Period:</span>
                    <span className="ml-2 font-medium">{metrics.period}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Start:</span>
                    <span className="ml-2 font-medium">{new Date(metrics.time_range.start).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">End:</span>
                    <span className="ml-2 font-medium">{new Date(metrics.time_range.end).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ) : (
              <Skeleton className="h-32" />
            )}
          </AdminCard>
        </div>
      </div>
    </div>
  )
}

